/**
 * MatzStore Beauty theme — vanilla JS / Web Components.
 * No build step, no framework runtime: progressive enhancement over
 * server-rendered Liquid markup, matching the interactions from the
 * original React source (mega menu, quick-shop carousel, predictive
 * search, product gallery/variants/tabs, hair quiz).
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------
     Header mega menu (desktop hover/click + mobile accordion)
     --------------------------------------------------------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll("[data-menu-trigger]"));
      this.panels = Array.from(this.querySelectorAll("[data-menu-panel]"));

      this.triggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          const key = trigger.dataset.menuTrigger;
          const isOpen = trigger.getAttribute("aria-expanded") === "true";
          // Linked triggers navigate once their panel is open (mouse users
          // open it on hover); a first tap on touch just opens the panel.
          if (trigger.tagName === "A" && isOpen) return;
          e.preventDefault();
          this.closeAll();
          if (!isOpen) this.open(key);
        });
        trigger.addEventListener("mouseenter", () => this.open(trigger.dataset.menuTrigger));
      });

      this.addEventListener("mouseleave", () => this.closeAll());

      // Hovering anything else in the header bar (links without a dropdown,
      // logo, search/cart icons) closes an open panel. Only interactive
      // elements count, so the gap between a trigger and its panel doesn't.
      this.querySelectorAll(
        ".header-row a:not([data-menu-trigger]), .header-row button:not([data-menu-trigger])"
      ).forEach((el) => el.addEventListener("mouseenter", () => this.closeAll()));

      document.addEventListener("click", (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.closeAll();
      });

      // Mobile drawer lives outside <header-menu> (see header.liquid), so it's
      // looked up on the document rather than on this element.
      this.mobileToggle = document.querySelector("[data-mobile-menu-toggle]");
      this.mobileMenu = document.querySelector("[data-mobile-menu]");
      this.mobileBackdrop = document.querySelector("[data-mobile-backdrop]");
      if (this.mobileToggle && this.mobileMenu) {
        this.mobileToggle.addEventListener("click", () => this.setMobileOpen(true));
        this.mobileBackdrop?.addEventListener("click", () => this.setMobileOpen(false));
        this.mobileMenu.querySelector("[data-mobile-menu-close]")?.addEventListener("click", () => this.setMobileOpen(false));
        document.addEventListener("keydown", (e) => {
          if (e.key === "Escape" && this.mobileMenu.classList.contains("is-open")) this.setMobileOpen(false);
        });

        this.mobileMenu.querySelectorAll("[data-mobile-panel-open]").forEach((btn) => {
          btn.addEventListener("click", () => this.showMobilePanel(btn.dataset.mobilePanelOpen));
        });
        this.mobileMenu.querySelectorAll("[data-mobile-panel-back]").forEach((btn) => {
          btn.addEventListener("click", () => this.showMobilePanel("root"));
        });
      }
    }
    setMobileOpen(open) {
      this.mobileMenu.classList.toggle("is-open", open);
      this.mobileBackdrop?.classList.toggle("is-open", open);
      this.mobileMenu.setAttribute("aria-hidden", String(!open));
      this.mobileToggle.setAttribute("aria-expanded", String(open));
      document.body.style.overflow = open ? "hidden" : "";
      // Start from the top level next time; wait for the slide-out to finish.
      if (!open) setTimeout(() => this.showMobilePanel("root"), 300);
    }
    showMobilePanel(key) {
      this.mobileMenu.querySelectorAll("[data-mobile-panel]").forEach((panel) => {
        const active = panel.dataset.mobilePanel === key;
        panel.classList.toggle("is-active", active);
        if (active) panel.scrollTop = 0;
      });
      this.mobileMenu.classList.toggle("is-sub-open", key !== "root");
    }
    open(key) {
      this.triggers.forEach((t) => t.setAttribute("aria-expanded", String(t.dataset.menuTrigger === key)));
      this.panels.forEach((p) => p.classList.toggle("is-open", p.dataset.menuPanel === key));
    }
    closeAll() {
      this.triggers.forEach((t) => t.setAttribute("aria-expanded", "false"));
      this.panels.forEach((p) => p.classList.remove("is-open"));
    }
  }
  customElements.define("header-menu", HeaderMenu);

  /* ---------------------------------------------------------
     Predictive search overlay
     --------------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("[data-search-input]");
      this.resultsEl = this.querySelector("[data-search-results]");
      this.form = this.querySelector("[data-search-form]");
      this.debounceTimer = null;
      this.noResultsText = this.dataset.noResultsText || 'Ingen produkter matcher "[query]".';
      this.viewAllText = this.dataset.viewAllText || 'Se alle resultater for "[query]"';
      this.searchUrl = this.dataset.searchUrl || "/search";
      this.limit = Math.min(Number(this.dataset.limit) || 8, 10);
      this.fields = this.dataset.fields || "title,product_type,variants.title,vendor,tag";
      this.showCollections = this.dataset.showCollections !== "false";
      this.showSuggestions = this.dataset.showSuggestions !== "false";
      // Kollektioner der aldrig vises som resultat (Header → Søgning). Kun selve
      // kollektionen skjules — dens produkter kan stadig findes.
      this.hiddenCollections = parseCommaList(this.dataset.hiddenCollections);
      this.hiddenCollectionTerms = parseCommaList(this.dataset.hiddenCollectionTerms);
      // Guards against out-of-order responses: a slow reply for "cond" must not
      // overwrite the newer results for "conditioner".
      this.requestId = 0;
      this.controller = null;

      document.querySelectorAll("[data-search-open]").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          this.open();
        });
      });
      this.querySelectorAll("[data-search-close]").forEach((btn) => btn.addEventListener("click", () => this.close()));
      this.addEventListener("click", (e) => {
        if (e.target === this) this.close();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.classList.contains("is-open")) this.close();
      });

      this.input?.addEventListener("input", () => {
        clearTimeout(this.debounceTimer);
        const q = this.input.value.trim();
        if (!q) {
          this.resultsEl.innerHTML = "";
          return;
        }
        this.debounceTimer = setTimeout(() => this.fetchResults(q), 150);
      });
    }
    open() {
      this.classList.add("is-open");
      document.body.style.overflow = "hidden";
      setTimeout(() => this.input?.focus(), 50);
    }
    close() {
      this.classList.remove("is-open");
      document.body.style.overflow = "";
    }
    async fetchResults(query) {
      const types = ["product"];
      if (this.showCollections) types.push("collection");
      if (this.showSuggestions) types.push("query");
      const params = new URLSearchParams({
        q: query,
        "resources[type]": types.join(","),
        "resources[limit]": String(this.limit),
        // Which product data Shopify matches against. Title/type/vendor alone
        // misses a lot on a Danish shop with English product names — tags (and
        // optionally the description) widen the net a good deal.
        "resources[options][fields]": this.fields,
        // Sold-out products are listed last by default, or left out entirely
        // when Tema-indstillinger → Udsolgte produkter hides them.
        "resources[options][unavailable_products]": this.dataset.hideSoldOut === "true" ? "hide" : "last",
      });
      this.controller?.abort();
      this.controller = new AbortController();
      const requestId = ++this.requestId;
      this.resultsEl.setAttribute("aria-busy", "true");
      try {
        const res = await fetch(`/search/suggest.json?${params.toString()}`, { signal: this.controller.signal });
        const data = await res.json();
        if (requestId !== this.requestId) return;
        this.renderResults(query, data?.resources?.results || {});
      } catch (err) {
        if (err.name === "AbortError") return;
        this.resultsEl.innerHTML = "";
      } finally {
        if (requestId === this.requestId) this.resultsEl.removeAttribute("aria-busy");
      }
    }
    isHiddenCollection(collection) {
      const urlHandle = String(collection.url || "").split("?")[0].split("/").filter(Boolean).pop() || "";
      const handle = String(collection.handle || urlHandle).toLowerCase();
      const title = String(collection.title || "").toLowerCase();
      if (this.hiddenCollections.includes(handle)) return true;
      return this.hiddenCollectionTerms.some((term) => handle.includes(term) || title.includes(term));
    }
    renderResults(query, results) {
      const products = results.products || [];
      const collections = (results.collections || []).filter((c) => !this.isHiddenCollection(c)).slice(0, 4);
      const queries = (results.queries || [])
        .filter((q) => q.text && q.text.toLowerCase() !== query.toLowerCase())
        .slice(0, 5);

      if (!products.length && !collections.length && !queries.length) {
        const message = this.noResultsText.replace("[query]", escapeHtml(query));
        this.resultsEl.innerHTML = `<div class="empty-state" style="padding-block:1.5rem;">
          <p>${message}</p>
        </div>`;
        return;
      }

      const searchLink = (term) => `${this.searchUrl}?q=${encodeURIComponent(term)}&type=product`;
      let html = "";

      if (queries.length) {
        html += `
          <div class="eyebrow" style="margin-bottom:0.6rem;">${escapeHtml(this.dataset.labelSuggestions || "Forslag")}</div>
          <div class="search-suggestions">
            ${queries
              .map((q) => `<a class="chip search-suggestions__item" href="${searchLink(q.text)}">${highlightMatch(q.text, query)}</a>`)
              .join("")}
          </div>`;
      }

      if (products.length) {
        html += `
          <div class="eyebrow" style="margin-bottom:0.75rem;">${escapeHtml(this.dataset.labelProducts || "Produkter")}</div>
          <ul>
            ${products
              .map(
                (p) => `
              <li>
                <a class="search-result" href="${p.url}">
                  <span class="search-result__image">${p.featured_image ? `<img src="${p.featured_image.url}" alt="" class="object-cover">` : ""}</span>
                  <span style="min-width:0;flex:1;">
                    <span style="display:block;font-size:0.625rem;text-transform:uppercase;letter-spacing:0.15em;opacity:0.6;">${escapeHtml(p.vendor || "")}</span>
                    <span style="display:block;font-size:0.875rem;font-weight:500;">${highlightMatch(p.title, query)}</span>
                  </span>
                  <span class="font-display" style="color:rgb(var(--color-accent));">${formatMoneyFromDecimal(p.price)}</span>
                </a>
              </li>`
              )
              .join("")}
          </ul>`;
      }

      if (collections.length) {
        html += `
          <div class="eyebrow" style="margin:1.25rem 0 0.6rem;">${escapeHtml(this.dataset.labelCollections || "Kollektioner")}</div>
          <div class="search-suggestions">
            ${collections
              .map((c) => `<a class="chip search-suggestions__item" href="${c.url}">${highlightMatch(c.title, query)}</a>`)
              .join("")}
          </div>`;
      }

      html += `
        <a href="${searchLink(query)}" class="link-underline" style="display:inline-block;margin-top:1rem;font-size:0.875rem;font-weight:500;">
          ${this.viewAllText.replace("[query]", escapeHtml(query))} →
        </a>`;

      this.resultsEl.innerHTML = html;
    }
  }
  customElements.define("predictive-search", PredictiveSearch);

  function parseCommaList(value) {
    return String(value || "")
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  /* Marks what the customer typed inside a result, so it is obvious why a
     result matched ("True Soft <mark>Condi</mark>tioner"). */
  function highlightMatch(text, query) {
    const safe = escapeHtml(text);
    const terms = String(query)
      .trim()
      .split(/\s+/)
      .filter((t) => t.length > 1)
      .map((t) => escapeHtml(t).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!terms.length) return safe;
    return safe.replace(new RegExp(`(${terms.join("|")})`, "gi"), "<mark>$1</mark>");
  }

  function formatMoneyFromDecimal(decimalString) {
    const amount = parseFloat(decimalString);
    if (Number.isNaN(amount)) return "";
    const hasCents = Math.round(amount * 100) % 100 !== 0;
    const formatted = amount.toLocaleString("da-DK", {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    });
    return `${formatted} kr`;
  }

  /* ---------------------------------------------------------
     Hero slider — crossfades between image/video slides, autoplay
     (paused on hover), prev/next arrows, dots, and a per-video pause
     button independent of the slide-advance timer.
     --------------------------------------------------------- */
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll("[data-hero-slide]"));
      if (!this.slides.length) return;
      this.dots = Array.from(this.querySelectorAll("[data-hero-dot]"));
      this.prevBtn = this.querySelector("[data-hero-prev]");
      this.nextBtn = this.querySelector("[data-hero-next]");
      this.index = 0;
      this.intervalMs = parseInt(this.dataset.interval, 10) || 7000;
      this.timer = null;

      this.prevBtn?.addEventListener("click", () => this.go(this.index - 1));
      this.nextBtn?.addEventListener("click", () => this.go(this.index + 1));
      this.dots.forEach((dot, i) => dot.addEventListener("click", () => this.go(i)));

      this.addEventListener("mouseenter", () => this.stopAutoplay());
      this.addEventListener("mouseleave", () => this.startAutoplay());

      this.querySelectorAll("[data-hero-pause]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const video = btn.closest("[data-hero-slide]")?.querySelector("video");
          if (!video) return;
          if (video.paused) {
            video.play().catch(() => {});
            btn.classList.remove("is-paused");
          } else {
            video.pause();
            btn.classList.add("is-paused");
          }
        });
      });

      this.render();
      this.startAutoplay();
    }
    disconnectedCallback() {
      this.stopAutoplay();
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.render();
    }
    render() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", String(!active));
        const video = slide.querySelector("video");
        if (!video) return;
        const pauseBtn = slide.querySelector("[data-hero-pause]");
        if (active) {
          video.play().catch(() => {});
          pauseBtn?.classList.remove("is-paused");
        } else {
          video.pause();
        }
      });
      this.dots.forEach((dot, i) => dot.classList.toggle("is-active", i === this.index));
    }
    startAutoplay() {
      this.stopAutoplay();
      if (this.slides.length < 2) return;
      this.timer = window.setInterval(() => this.go(this.index + 1), this.intervalMs);
    }
    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }
  customElements.define("hero-slider", HeroSlider);

  /* ---------------------------------------------------------
     Quick-shop carousel (homepage)
     --------------------------------------------------------- */
  class QuickShopCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-carousel-track]");
      this.dotsContainer = this.querySelector("[data-carousel-dots]");
      this.prevBtn = this.querySelector("[data-carousel-prev]");
      this.nextBtn = this.querySelector("[data-carousel-next]");
      if (!this.track) return;

      this.prevBtn?.addEventListener("click", () => this.scrollByPage(-1));
      this.nextBtn?.addEventListener("click", () => this.scrollByPage(1));
      this.track.addEventListener("scroll", () => this.updateDots(), { passive: true });
      window.addEventListener("resize", () => this.buildDots());
      this.buildDots();
    }
    scrollByPage(dir) {
      this.track.scrollBy({ left: dir * Math.round(this.track.clientWidth * 0.8), behavior: "smooth" });
    }
    goTo(i) {
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: "smooth" });
    }
    buildDots() {
      if (!this.dotsContainer || this.track.clientWidth <= 0) return;
      const pageCount = Math.max(1, Math.ceil(this.track.scrollWidth / this.track.clientWidth));
      this.dotsContainer.innerHTML = "";
      if (pageCount <= 1) return;
      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "quick-shop__dot";
        dot.setAttribute("aria-label", `Gå til side ${i + 1}`);
        dot.addEventListener("click", () => this.goTo(i));
        this.dotsContainer.appendChild(dot);
      }
      this.updateDots();
    }
    updateDots() {
      const dots = this.dotsContainer ? Array.from(this.dotsContainer.children) : [];
      if (!dots.length) return;
      const page = Math.round(this.track.scrollLeft / this.track.clientWidth);
      dots.forEach((dot, i) => dot.classList.toggle("is-active", i === page));
    }
  }
  customElements.define("quick-shop-carousel", QuickShopCarousel);

  /* ---------------------------------------------------------
     USP-bjælken — automatisk slider, men kun på mobil. Over
     brydepunktet er samme markup et grid (se CSS), og så kører
     autoplay slet ikke. Pauser mens kunden selv swiper, og når
     fanen er skjult; respekterer prefers-reduced-motion.
     --------------------------------------------------------- */
  class UspSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-usp-track]");
      this.dotsContainer = this.querySelector("[data-usp-dots]");
      this.items = Array.from(this.querySelectorAll(".usp-item"));
      if (!this.track || this.dataset.slider === "false" || this.items.length < 2) return;

      this.interval = Number(this.dataset.interval) || 5000;
      this.timer = null;
      this.isMobile = window.matchMedia("(max-width: 767px)");
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

      this.buildDots();
      this.track.addEventListener("scroll", () => this.syncDots(), { passive: true });
      this.track.addEventListener("pointerdown", () => this.stop());
      this.track.addEventListener("pointerup", () => this.start());
      this.track.addEventListener("pointercancel", () => this.start());
      document.addEventListener("visibilitychange", () => (document.hidden ? this.stop() : this.start()));
      this.isMobile.addEventListener("change", () => this.start());
      this.start();
    }
    disconnectedCallback() {
      this.stop();
    }
    get index() {
      return this.track.clientWidth ? Math.round(this.track.scrollLeft / this.track.clientWidth) : 0;
    }
    start() {
      this.stop();
      if (!this.isMobile.matches || this.reduceMotion.matches) return;
      this.timer = setInterval(() => this.goTo((this.index + 1) % this.items.length), this.interval);
    }
    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
    goTo(i) {
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: "smooth" });
    }
    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = "";
      this.items.forEach((item, i) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.className = "usp-bar__dot" + (i === 0 ? " is-active" : "");
        dot.setAttribute("aria-label", `Gå til fordel ${i + 1}`);
        dot.addEventListener("click", () => {
          this.goTo(i);
          this.start();
        });
        this.dotsContainer.appendChild(dot);
      });
    }
    syncDots() {
      if (!this.dotsContainer) return;
      const current = this.index;
      Array.from(this.dotsContainer.children).forEach((dot, i) => dot.classList.toggle("is-active", i === current));
    }
  }
  customElements.define("usp-slider", UspSlider);

  /* ---------------------------------------------------------
     Bestsellers — reveal a random subset of the server-rendered
     (hidden) product pool on every page load.
     --------------------------------------------------------- */
  class BestsellersRandom extends HTMLElement {
    connectedCallback() {
      const items = Array.from(this.querySelectorAll("[hidden]"));
      const count = parseInt(this.dataset.count, 10) || 4;
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
      items.slice(0, count).forEach((item) => item.removeAttribute("hidden"));
    }
  }
  customElements.define("bestsellers-random", BestsellersRandom);

  /* ---------------------------------------------------------
     Collection filters — tap-friendly slide-in drawer on mobile/tablet.
     At desktop widths the panel is a static sidebar (see CSS), so open()/
     close() simply become no-ops there since the toggle button is hidden.
     --------------------------------------------------------- */
  class FilterDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector("[data-filters-panel]");
      this.backdrop = this.querySelector("[data-filters-backdrop]");
      document.querySelectorAll("[data-filters-open]").forEach((btn) => {
        btn.addEventListener("click", () => this.open());
      });
      this.querySelectorAll("[data-filters-close]").forEach((btn) => {
        btn.addEventListener("click", () => this.close());
      });
      this.backdrop?.addEventListener("click", () => this.close());
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen()) this.close();
      });
    }
    isOpen() {
      return this.panel?.classList.contains("is-open");
    }
    open() {
      this.panel?.classList.add("is-open");
      this.backdrop?.classList.add("is-open");
      document.body.style.overflow = "hidden";
    }
    close() {
      this.panel?.classList.remove("is-open");
      this.backdrop?.classList.remove("is-open");
      document.body.style.overflow = "";
    }
  }
  customElements.define("filter-drawer", FilterDrawer);

  /* ---------------------------------------------------------
     Product gallery (thumbnail -> main image)
     --------------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.thumbs = Array.from(this.querySelectorAll("[data-gallery-thumb]"));
      this.slides = Array.from(this.querySelectorAll("[data-gallery-slide]"));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => this.show(thumb.dataset.galleryThumb));
      });
    }
    // Every media item (image, video, YouTube/Vimeo) is its own slide; only
    // the active one is shown, and a video that's hidden is paused.
    show(index) {
      this.thumbs.forEach((t) => t.classList.toggle("is-active", t.dataset.galleryThumb === index));
      this.slides.forEach((slide) => {
        const active = slide.dataset.gallerySlide === index;
        if (!active && !slide.hidden) this.pauseMedia(slide);
        slide.hidden = !active;
      });
    }
    pauseMedia(slide) {
      slide.querySelectorAll("video").forEach((v) => v.pause());
      slide.querySelectorAll("iframe").forEach((frame) => {
        // YouTube (enablejsapi=1) and Vimeo both accept a pause command.
        frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
        frame.contentWindow?.postMessage('{"method":"pause"}', "*");
      });
    }
  }
  customElements.define("product-gallery", ProductGallery);

  /* ---------------------------------------------------------
     Product form: variant picker + quantity + add to cart
     --------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.variantInput = this.querySelector("[data-variant-input]") || this.querySelector("input[name='id']");
      this.priceEl = this.querySelector("[data-product-price]");
      this.comparePriceEl = this.querySelector("[data-product-compare-price]");
      this.qtyInput = this.querySelector("[data-qty-input]");
      this.submitBtn = this.querySelector("[data-add-to-cart]");
      this.submitLabel = this.querySelector("[data-add-to-cart-label]");

      this.querySelectorAll("[data-variant-pill]").forEach((pill) => {
        pill.addEventListener("click", () => {
          if (pill.disabled) return;
          this.querySelectorAll("[data-variant-pill]").forEach((p) => p.classList.remove("is-selected"));
          pill.classList.add("is-selected");
          if (this.variantInput) this.variantInput.value = pill.dataset.variantId;
          if (this.priceEl) this.priceEl.textContent = pill.dataset.variantPrice;
          if (this.comparePriceEl) {
            this.comparePriceEl.style.display = pill.dataset.variantCompare ? "" : "none";
            this.comparePriceEl.textContent = pill.dataset.variantCompare || "";
          }
          // %-tilbudsbadge sits in the gallery (outside this element) and
          // follows the selected variant's own discount.
          const percent = pill.dataset.variantSalePercent;
          const salePercentEl = document.querySelector(".product-gallery [data-sale-percent]");
          if (salePercentEl) {
            salePercentEl.closest("[data-sale-badge]").hidden = !percent;
            if (percent) salePercentEl.textContent = percent;
          }
          const savingEl = this.querySelector("[data-product-saving]");
          if (savingEl) {
            savingEl.hidden = !percent;
            if (percent) savingEl.querySelector("[data-product-saving-percent]").textContent = percent;
          }
          const stickyPrice = document.querySelector("[data-sticky-price]");
          if (stickyPrice) stickyPrice.textContent = pill.dataset.variantPrice;
          this.updateSubmitLabel();
        });
      });

      this.querySelectorAll("[data-qty-stepper]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const dir = Number(btn.dataset.qtyStepper);
          const next = Math.max(1, Number(this.qtyInput.value || 1) + dir);
          this.qtyInput.value = String(next);
          this.updateSubmitLabel();
        });
      });
      this.qtyInput?.addEventListener("change", () => this.updateSubmitLabel());

      this.form?.addEventListener("submit", (e) => {
        e.preventDefault();
        this.addToCart();
      });

      this.updateSubmitLabel();
    }
    updateSubmitLabel() {
      if (!this.submitLabel) return;
      const qty = Number(this.qtyInput?.value || 1);
      const selectedPill = this.querySelector("[data-variant-pill].is-selected");
      const stickyLabelEl = document.querySelector("[data-sticky-add-to-cart-label]");
      // Sold out: keep the merchant's label (Tema-indstillinger → Udsolgte
      // produkter) instead of the "Læg i kurv — X kr" price label.
      const available = selectedPill
        ? selectedPill.dataset.variantAvailable !== "false"
        : this.dataset.available !== "false";
      if (!available) {
        const soldOutLabel = this.dataset.soldOutLabel || "Udsolgt";
        this.submitLabel.textContent = soldOutLabel;
        if (this.submitBtn) this.submitBtn.disabled = true;
        if (stickyLabelEl) {
          stickyLabelEl.textContent = soldOutLabel;
          const stickyBtn = stickyLabelEl.closest("button");
          if (stickyBtn) stickyBtn.disabled = true;
        }
        return;
      }
      const unitPrice = Number(selectedPill?.dataset.variantPriceCents || this.dataset.priceCents || 0);
      const total = ((unitPrice * qty) / 100).toLocaleString("da-DK", { maximumFractionDigits: 0 });
      const label = `Læg i kurv — ${total} kr`;
      this.submitLabel.textContent = label;
      if (stickyLabelEl) stickyLabelEl.textContent = label;
    }
    /* Variant-id'et, der skal i kurven: det skjulte felt først, ellers den
       valgte variant-knap. Uden et gyldigt id svarer Shopify "Cannot find
       variant", så vi stopper hellere før kaldet med en tydelig fejl. */
    selectedVariantId() {
      const fromInput = Number(this.variantInput?.value);
      if (fromInput > 0) return fromInput;
      const fromPill = Number(this.querySelector("[data-variant-pill].is-selected")?.dataset.variantId);
      return fromPill > 0 ? fromPill : null;
    }
    async addToCart() {
      if (!window.MatzCart) return;
      const variantId = this.selectedVariantId();
      if (!variantId) {
        console.error("Kunne ikke finde variant-id på produktformularen — varen blev ikke lagt i kurven.");
        return;
      }
      this.submitBtn.disabled = true;
      try {
        await window.MatzCart.add({
          id: variantId,
          quantity: Number(this.qtyInput?.value || 1),
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.submitBtn.disabled = false;
      }
    }
  }
  customElements.define("product-form", ProductForm);

  /* ---------------------------------------------------------
     Sticky mobile "add to cart" bar — appears once the real add-to-cart
     button has scrolled out of view above the viewport. The bar's own
     button submits the same form via the form="" attribute, so variant/
     quantity state and the AJAX add-to-cart logic are never duplicated.
     --------------------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (!("IntersectionObserver" in window)) return;
      const formId = this.dataset.targetForm;
      const target = formId && document.querySelector(`#${formId} [data-add-to-cart]`);
      if (!target) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          const scrolledPast = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
          this.classList.toggle("is-visible", scrolledPast);
          document.body.classList.toggle("has-sticky-atc", scrolledPast);
        },
        { threshold: 0 }
      );
      observer.observe(target);
    }
  }
  customElements.define("sticky-atc", StickyAtc);

  /* ---------------------------------------------------------
     Simple "add to cart" buttons outside the PDP (bestsellers,
     package cards) — quantity 1, fixed variant id.
     --------------------------------------------------------- */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-quick-add]");
    if (!btn || !window.MatzCart) return;
    e.preventDefault();
    const id = Number(btn.dataset.quickAdd);
    if (!id) return;
    window.MatzCart.add({ id, quantity: 1 }).catch((err) => console.error(err));
  });

  /* ---------------------------------------------------------
     Product tabs (description / how-to / ingredients)
     --------------------------------------------------------- */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll("[data-tab-trigger]").forEach((trigger) => {
        trigger.addEventListener("click", () => {
          const key = trigger.dataset.tabTrigger;
          this.querySelectorAll("[data-tab-trigger]").forEach((t) => t.classList.toggle("is-active", t === trigger));
          this.querySelectorAll("[data-tab-panel]").forEach((p) => p.classList.toggle("is-active", p.dataset.tabPanel === key));
        });
      });
    }
  }
  customElements.define("product-tabs", ProductTabs);

  /* ---------------------------------------------------------
     Hair quiz — 3 single-select steps, then an instant
     rule-matched product-recommendation results step.
     --------------------------------------------------------- */
  class HairQuiz extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll("[data-quiz-step]"));
      this.resultsStep = this.querySelector("[data-quiz-results]");
      this.resultSets = Array.from(this.querySelectorAll("[data-quiz-result]"));
      this.progressBar = this.querySelector("[data-quiz-progress]");
      this.stepLabel = this.querySelector("[data-quiz-step-label]");
      this.backBtn = this.querySelector("[data-quiz-back]");
      this.nextBtn = this.querySelector("[data-quiz-next]");
      this.nav = this.querySelector("[data-quiz-nav]");
      this.retakeBtn = this.querySelector("[data-quiz-retake]");
      this.answers = {};
      this.current = 0;
      this.total = this.steps.length;

      this.querySelectorAll("[data-quiz-option]").forEach((opt) => {
        opt.addEventListener("click", () => this.selectOption(opt));
      });
      this.backBtn?.addEventListener("click", () => this.go(this.current - 1));
      this.nextBtn?.addEventListener("click", () => this.go(this.current + 1));
      this.retakeBtn?.addEventListener("click", () => this.retake());

      this.render();
    }
    selectOption(opt) {
      const step = opt.closest("[data-quiz-step]");
      const stepId = step.dataset.quizStep;
      const value = opt.dataset.quizOption;

      this.answers[stepId] = value;
      step.querySelectorAll("[data-quiz-option]").forEach((o) => o.classList.toggle("is-selected", o === opt));
      setTimeout(() => this.go(this.current + 1), 220);
      this.updateNav();
    }
    canContinue() {
      const step = this.steps[this.current];
      if (!step) return true;
      return !!this.answers[step.dataset.quizStep];
    }
    go(index) {
      this.current = Math.max(0, Math.min(index, this.total));
      this.render();
      if (this.current === this.total) this.showResults();
    }
    retake() {
      this.answers = {};
      this.querySelectorAll("[data-quiz-option].is-selected").forEach((o) => o.classList.remove("is-selected"));
      this.resultsStep?.classList.remove("is-active");
      this.go(0);
    }
    render() {
      this.steps.forEach((step, i) => step.classList.toggle("is-active", i === this.current));
      const isResults = this.current === this.total;
      this.resultsStep?.classList.toggle("is-active", isResults);

      const progress = Math.round(((this.current + (isResults ? 1 : 0)) / (this.total + 1)) * 100);
      if (this.progressBar) this.progressBar.style.width = `${progress}%`;
      if (this.stepLabel) {
        this.stepLabel.textContent = isResults ? "Dine anbefalinger" : `Trin ${this.current + 1} af ${this.total}`;
      }
      if (this.nav) this.nav.style.display = isResults ? "none" : "flex";
      this.updateNav();
    }
    updateNav() {
      if (this.backBtn) this.backBtn.disabled = this.current === 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.canContinue();
    }
    showResults() {
      const type = this.answers.type;
      const challenge = this.answers.challenge;
      const goal = this.answers.goal;

      const match = this.resultSets.find((set) => {
        const mType = set.dataset.matchType;
        const mChallenge = set.dataset.matchChallenge;
        const mGoal = set.dataset.matchGoal;
        return (
          (mType === "any" || mType === type) &&
          (mChallenge === "any" || mChallenge === challenge) &&
          (mGoal === "any" || mGoal === goal)
        );
      });
      const fallback = this.resultSets[this.resultSets.length - 1];
      const winner = match || fallback;

      this.resultSets.forEach((set) => set.classList.toggle("is-active", set === winner));
    }
  }
  customElements.define("hair-quiz", HairQuiz);

  /* ---------------------------------------------------------
     Bundle builder — pick N products, live subtotal/discount
     preview, add all as line items in one go. The actual
     percentage discount is applied by Shopify's own automatic
     discounts (configured in Admin); this only estimates it
     client-side for the summary panel.
     --------------------------------------------------------- */
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.tier1Qty = Number(this.dataset.tier1Qty || 3);
      this.tier1Percent = Number(this.dataset.tier1Percent || 10);
      this.tier2Qty = Number(this.dataset.tier2Qty || 4);
      this.tier2Percent = Number(this.dataset.tier2Percent || 15);

      this.countLabel = this.querySelector("[data-bundle-count-label]");
      this.messageEl = this.querySelector("[data-bundle-message]");
      this.progressBar = this.querySelector("[data-bundle-progress]");
      this.subtotalEl = this.querySelector("[data-bundle-subtotal]");
      this.totalEl = this.querySelector("[data-bundle-total]");
      this.submitBtn = this.querySelector("[data-bundle-submit]");

      this.selected = new Map();

      this.querySelectorAll("[data-bundle-item]").forEach((card) => {
        const toggle = card.querySelector("[data-bundle-toggle]");
        toggle?.addEventListener("click", () => {
          if (toggle.disabled) return;
          const id = card.dataset.variantId;
          if (this.selected.has(id)) {
            this.selected.delete(id);
            card.classList.remove("is-selected");
          } else {
            this.selected.set(id, {
              id,
              price: Number(card.dataset.price),
            });
            card.classList.add("is-selected");
          }
          this.render();
        });
      });

      this.submitBtn?.addEventListener("click", () => this.addBundleToCart());

      this.render();
    }
    formatMoney(cents) {
      return `${Math.round(cents / 100).toLocaleString("da-DK")} kr`;
    }
    currentDiscount(count) {
      if (count >= this.tier2Qty) return this.tier2Percent;
      if (count >= this.tier1Qty) return this.tier1Percent;
      return 0;
    }
    render() {
      const count = this.selected.size;
      const subtotal = Array.from(this.selected.values()).reduce((sum, i) => sum + i.price, 0);
      const discount = this.currentDiscount(count);
      const total = subtotal * (1 - discount / 100);

      if (this.countLabel) {
        this.countLabel.textContent = count === 0 ? "0 produkter valgt" : `${count} ${count === 1 ? "produkt" : "produkter"} valgt`;
      }

      if (this.messageEl) {
        if (count < this.tier1Qty) {
          const remaining = this.tier1Qty - count;
          this.messageEl.innerHTML = `Vælg mindst ${this.tier1Qty} for at låse rabat op<br>Vælg ${remaining} mere → spar ${this.tier1Percent}%`;
        } else if (count < this.tier2Qty) {
          const remaining = this.tier2Qty - count;
          this.messageEl.innerHTML = `Du har ${this.tier1Percent}% rabat!<br>Vælg ${remaining} mere → spar ${this.tier2Percent}%`;
        } else {
          this.messageEl.textContent = `Du har opnået den bedste rabat: ${this.tier2Percent}%!`;
        }
      }

      if (this.progressBar) {
        const progress = Math.min(100, (count / this.tier2Qty) * 100);
        this.progressBar.style.width = `${progress}%`;
      }

      if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(subtotal);
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);

      if (this.submitBtn) this.submitBtn.disabled = count < this.tier1Qty;
    }
    async addBundleToCart() {
      if (!window.MatzCart || this.selected.size === 0) return;
      this.submitBtn.disabled = true;
      try {
        await window.MatzCart.add(
          Array.from(this.selected.values()).map((item) => ({ id: Number(item.id), quantity: 1 }))
        );
      } catch (err) {
        console.error(err);
        this.submitBtn.disabled = false;
      }
    }
  }
  customElements.define("bundle-builder", BundleBuilder);
})();
